import { useLocation } from 'react-router-dom';
import { useRef, useState } from 'react';
import { addToLocalStorage } from '@guestyci/gfh-utils';
import featureToggleInit from '@guestyci/feature-toggle-fe/init';

import env from 'config/env';
import auth, { AutologinQuery, ILoginParams } from 'API/handlers/auth';
import { AxiosPromise } from 'axios';

function errorMessages(errors: any) {
  let message;

  switch (errors.errorCode) {
    case 16: {
      message = 'Auto Login Failed';
      break;
    }
    case 17: {
      message = 'Invalid session';
      break;
    }
    default: {
      message = 'Request failed';
    }
  }

  return errors.errorMessage || message;
}

export default function useAuthorizationModel() {
  const [isLoading, setLoading] = useState(false);
  const [responseSuccessMessage, setSuccessMessage] = useState('');
  const [responseErrorMessage, setErrorMessage] = useState('');
  const { search } = useLocation();
  const queryParams = useRef(new URLSearchParams(search));
  const isZendeskAuthorization = useRef(
    queryParams.current.get('zendeskSso') ?? ''
  );
  const requestLogin = async (loginFn: () => AxiosPromise) => {
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    const { data } = await loginFn();

    setLoading(false);

    if (data.success) {
      setSuccessMessage(data.message);
    } else {
      setErrorMessage(errorMessages(data));
      const accountIsSuspended = data.errorCode === 16;
      if (accountIsSuspended) {
        return Promise.resolve(data); // Allow user to sign in. Will show him non-closable banner
      }
    }

    if (data.baseUserInfo?.accountId) {
      featureToggleInit({
        env,
        accountId: data.baseUserInfo.accountId,
      }).then((ft: { isEnabled: (arg: string) => boolean }) => {
        const gfhLockedFlow = ft.isEnabled('gfhLockedFlow');
        const gfhTrialTimerPopUp = ft.isEnabled('gfhTrialTimerPopUp');

        if (data.baseUserInfo?.inTrial && gfhLockedFlow && gfhTrialTimerPopUp) {
          addToLocalStorage({ showTrialDialogOnLogin: true });
        }
      });
    }

    return data.success ? Promise.resolve(data) : Promise.reject(data);
  };

  return {
    isLoading,
    responseSuccessMessage,
    responseErrorMessage,
    isZendeskAuthorization: isZendeskAuthorization.current,
    login: (data: ILoginParams) => requestLogin(() => auth.login(data)),
    autoLogin: (data: AutologinQuery, abortSignal?: AbortSignal) =>
      requestLogin(() => auth.autoLogin(data, abortSignal)),
    loginSSO: ({ credential }: { credential: string }) =>
      requestLogin(() =>
        auth.googleSSO({
          googleIdToken: credential,
          isSignIn: true,
        })
      ),
    resetPassword: async function handler(data: {
      email: string;
      token: string;
    }) {
      setLoading(true);
      setSuccessMessage('');
      setErrorMessage('');

      const response = await auth.resetPassword(data);

      setLoading(false);

      if (response.data.success) {
        setSuccessMessage('An email has been sent to your email address.');
      } else {
        setErrorMessage(errorMessages(response.data));
      }

      return response.data.success
        ? Promise.resolve(response.data)
        : Promise.reject(response.data);
    },
    tokenAuthenticate: async function handler(token: string) {
      const authResponse = await auth.tokenAuthenticate(token);
      return authResponse.status === 200
        ? Promise.resolve(authResponse.data)
        : Promise.reject(authResponse.data);
    },
    // Get mobile user full info using the JWT from the query string
    tokenCurrentUser: async function handler(token: string) {
      const userResponse = await auth.tokenCurrentUser(token);
      return userResponse.status === 200
        ? Promise.resolve(userResponse.data)
        : Promise.reject(userResponse.data);
    },
    ssoZendesk: async () => {
      setLoading(true);
      const data = await auth.zendeskSso();
      setLoading(false);

      return data.status === 200
        ? Promise.resolve(data.data)
        : Promise.reject(data);
    },
  };
}
